import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    DatePicker,
    Checkbox,
    Button,
    Row,
    Col,
    Typography,
    Space,
    Spin,
    message,
    Table,
    Tag,
    Popconfirm,
    Select,
    Modal,
} from "antd";
import dayjs from "dayjs";
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, UnorderedListOutlined } from "@ant-design/icons";
import firebase from "../../firebase";

const { Title } = Typography;
const { TextArea } = Input;

const COLLECTION_NAME = "notification";

const Notification = (props) => {
    const { db, history } = props;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [mode, setMode] = useState("list"); // "list" | "form"
    const [editingId, setEditingId] = useState(null);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [existingImageUrls, setExistingImageUrls] = useState([]);
    const [dateError, setDateError] = useState(false);
    const [baseOrgDetails, setBaseOrgDetails] = useState(null);
    const [pendingOrgChange, setPendingOrgChange] = useState(null);
    const [mapModalState, setMapModalState] = useState({
        open: false,
        record: null,
        category: null,
        facilities: [],
        checkedOrgIds: [],
        searchText: "",
        loading: false,
        saving: false,
    });

    // ===============================
    // Load all notifications & orgs
    // ===============================
    const loadNotifications = async () => {
        try {
            setLoading(true);
            const snapshot = await db.collection(COLLECTION_NAME).orderBy("createdAt", "desc").get();
            const list = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setNotifications(list);
        } catch (err) {
            console.error(err);
            message.error("お知らせの読み込み中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    const loadOrganizations = async () => {
        try {
            const snapshot = await db.collection("organizations").where("active", "==", true).get();
            const list = snapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name,
                company_name: doc.data().company_name || "",
                maker: !!doc.data().maker,
                reseller: !!doc.data().reseller,
                buyer: !!doc.data().buyer,
            }));
            setOrganizations(list);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (props.user && props.user.admin) {
            loadNotifications();
            loadOrganizations();
        }
    }, [db, props.user]);

    useEffect(() => {
        const fetchBaseOrg = async () => {
            const baseOrg = props.orgSeller || props.userOrg;
            if (baseOrg && baseOrg.id) {
                try {
                    const doc = await db.collection("organizations").doc(baseOrg.id).get();
                    if (doc.exists) {
                        setBaseOrgDetails({
                            id: doc.id,
                            ...doc.data()
                        });
                    }
                } catch (err) {
                    console.error("Error loading base organization details:", err);
                }
            } else {
                setBaseOrgDetails(null);
            }
        };
        fetchBaseOrg();
    }, [props.orgSeller, props.userOrg, db]);

    useEffect(() => {
        if (props.setBeforeOrgSellerChange) {
            props.setBeforeOrgSellerChange((newId, newName, proceed) => {
                const currentOrgId = baseOrgDetails?.id || props.orgSeller?.id || props.userOrg?.id;
                if (currentOrgId && newId !== currentOrgId) {
                    setPendingOrgChange({ id: newId, name: newName, proceed });
                } else {
                    proceed();
                }
            });
        }
        return () => {
            if (props.setBeforeOrgSellerChange) {
                props.setBeforeOrgSellerChange(null);
            }
        };
    }, [props.setBeforeOrgSellerChange, baseOrgDetails, props.orgSeller, props.userOrg]);

    const handleConfirmOrgChange = () => {
        if (pendingOrgChange && pendingOrgChange.proceed) {
            pendingOrgChange.proceed();
        }
        form.setFieldsValue({
            targetOrgsReseller: [],
            targetOrgsBuyer: [],
        });
        setPendingOrgChange(null);
    };

    const handleCancelOrgChange = () => {
        setPendingOrgChange(null);
    };

    // ===============================
    // Map Facilities Modal Handlers
    // ===============================
    const handleOpenMapModal = async (record, category) => {
        setMapModalState({
            open: true,
            record: record,
            category: category,
            facilities: [],
            checkedOrgIds: [],
            searchText: "",
            loading: true,
            saving: false,
        });

        try {
            let currentOrgs = organizations;
            if (!currentOrgs || currentOrgs.length === 0) {
                const orgsSnap = await db.collection("organizations").where("active", "==", true).get();
                currentOrgs = orgsSnap.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name,
                    company_name: doc.data().company_name || "",
                    maker: !!doc.data().maker,
                    reseller: !!doc.data().reseller,
                    buyer: !!doc.data().buyer,
                }));
                setOrganizations(currentOrgs);
            }

            let baseDoc = null;
            if (record.baseOrgId) {
                const docSnap = await db.collection("organizations").doc(record.baseOrgId).get();
                if (docSnap.exists) {
                    baseDoc = docSnap.data();
                }
            }

            let mappedList = [];
            if (category === "reseller") {
                if (baseDoc && baseDoc.sellers && Array.isArray(baseDoc.sellers) && baseDoc.sellers.length > 0) {
                    mappedList = baseDoc.sellers.map(s => s.organization);
                } else if (record.baseOrgSellers && Array.isArray(record.baseOrgSellers) && record.baseOrgSellers.length > 0) {
                    mappedList = record.baseOrgSellers;
                } else {
                    mappedList = currentOrgs.filter(o => o.reseller).map(o => o.id);
                }
            } else if (category === "buyer") {
                if (baseDoc && baseDoc.buyers && Array.isArray(baseDoc.buyers) && baseDoc.buyers.length > 0) {
                    mappedList = baseDoc.buyers.map(b => b.organization);
                } else if (record.baseOrgBuyers && Array.isArray(record.baseOrgBuyers) && record.baseOrgBuyers.length > 0) {
                    mappedList = record.baseOrgBuyers;
                } else {
                    mappedList = currentOrgs.filter(o => o.buyer).map(o => o.id);
                }
            }

            const facilitiesDetails = mappedList.map(id => {
                const found = currentOrgs.find(o => o.id === id);
                return {
                    id: id,
                    company_name: found ? found.company_name : "",
                    name: found ? found.name : id,
                    maker: found ? found.maker : false,
                    reseller: found ? found.reseller : false,
                    buyer: found ? found.buyer : false,
                };
            });

            const currentSelected = category === "reseller" ? (record.targetOrgsReseller || []) : (record.targetOrgsBuyer || []);
            let initialChecked = [];
            if (currentSelected.length === 1 && currentSelected[0] === "NONE") {
                initialChecked = [];
            } else if (currentSelected.length === 0) {
                initialChecked = mappedList;
            } else {
                initialChecked = currentSelected;
            }

            setMapModalState(prev => ({
                ...prev,
                facilities: facilitiesDetails,
                checkedOrgIds: initialChecked,
                loading: false,
            }));
        } catch (err) {
            console.error(err);
            message.error("事業所一覧の取得中にエラーが発生しました。");
            setMapModalState(prev => ({ ...prev, loading: false }));
        }
    };

    const handleSaveMapModal = async () => {
        const { record, category, facilities, checkedOrgIds } = mapModalState;
        if (!record) return;

        setMapModalState(prev => ({ ...prev, saving: true }));

        try {
            const allFacilityIds = facilities.map(f => f.id);
            const isAllChecked = allFacilityIds.length > 0 && allFacilityIds.every(id => checkedOrgIds.includes(id));
            
            let newCategoryTargetOrgs = [];
            if (isAllChecked) {
                newCategoryTargetOrgs = [];
            } else if (checkedOrgIds.length === 0) {
                newCategoryTargetOrgs = ["NONE"];
            } else {
                newCategoryTargetOrgs = checkedOrgIds;
            }

            const existingResellers = record.targetOrgsReseller || [];
            const existingBuyers = record.targetOrgsBuyer || [];

            const updatedResellers = category === "reseller" ? newCategoryTargetOrgs : existingResellers;
            const updatedBuyers = category === "buyer" ? newCategoryTargetOrgs : existingBuyers;

            const combinedResellers = updatedResellers.filter(id => id !== "NONE");
            const combinedBuyers = updatedBuyers.filter(id => id !== "NONE");
            const newTargetOrgs = Array.from(new Set([...combinedResellers, ...combinedBuyers]));

            const updateData = {
                targetOrgs: newTargetOrgs,
                updatedAt: new Date(),
                updatedBy: props.user ? props.user.id : "",
                updatedByName: props.user ? `${props.user.last_name || ""} ${props.user.first_name || ""}`.trim() : ""
            };

            if (category === "reseller") {
                updateData.targetOrgsReseller = newCategoryTargetOrgs;
            } else if (category === "buyer") {
                updateData.targetOrgsBuyer = newCategoryTargetOrgs;
            }

            await db.collection(COLLECTION_NAME).doc(record.id).update(updateData);
            message.success("対象事業所を更新しました。");

            setMapModalState({
                open: false,
                record: null,
                category: null,
                facilities: [],
                checkedOrgIds: [],
                searchText: "",
                loading: false,
                saving: false,
            });

            loadNotifications();
        } catch (err) {
            console.error(err);
            message.error("保存中にエラーが発生しました。");
            setMapModalState(prev => ({ ...prev, saving: false }));
        }
    };

    // ===============================
    // Actions
    // ===============================
    const handleCreateClick = () => {
        setEditingId(null);
        setFilesToUpload([]);
        setExistingImageUrls([]);
        setDateError(false);
        form.resetFields();
        form.setFieldsValue({
            isActive: true,
            targetOrgsReseller: [],
            targetOrgsBuyer: [],
        });
        setMode("form");
    };

    const handleEditClick = (record) => {
        setEditingId(record.id);
        setFilesToUpload([]);
        setExistingImageUrls(record.imageUrls || []);
        setDateError(false);

        let resellerOrgs = record.targetOrgsReseller || [];
        let buyerOrgs = record.targetOrgsBuyer || [];

        // Fallback for legacy notifications created before targetOrgsReseller/targetOrgsBuyer were set
        if (!record.targetOrgsReseller && !record.targetOrgsBuyer && record.targetOrgs) {
            resellerOrgs = (record.targetOrgs || []).filter(id => {
                return (baseOrgDetails?.sellers || []).some(s => s.organization === id);
            });
            buyerOrgs = (record.targetOrgs || []).filter(id => {
                return (baseOrgDetails?.buyers || []).some(b => b.organization === id);
            });
        }

        form.setFieldsValue({
            title: record.title || "",
            body: record.body || "",
            displayStart: record.displayStart ? dayjs(record.displayStart.toDate()) : null,
            displayEnd: record.displayEnd ? dayjs(record.displayEnd.toDate()) : null,
            isActive: record.isActive !== false,
            targetOrgsReseller: resellerOrgs,
            targetOrgsBuyer: buyerOrgs,
        });
        setMode("form");
    };

    const deleteStorageFile = async (url) => {
        try {
            const fileRef = firebase.storage().refFromURL(url);
            await fileRef.delete();
        } catch (err) {
            console.error("Error deleting file from storage:", url, err);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const doc = await db.collection(COLLECTION_NAME).doc(id).get();
            if (doc.exists) {
                const data = doc.data();
                const urls = data.imageUrls || [];
                const deletePromises = urls.map(url => deleteStorageFile(url));
                await Promise.all(deletePromises);
            }
            await db.collection(COLLECTION_NAME).doc(id).delete();
            message.success("お知らせを削除しました。");
            loadNotifications();
        } catch (err) {
            console.error(err);
            message.error("削除中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (record) => {
        try {
            setLoading(true);
            const newActive = !record.isActive;
            await db.collection(COLLECTION_NAME).doc(record.id).update({
                isActive: newActive,
                updatedAt: new Date(),
                updatedBy: props.user ? props.user.id : "",
                updatedByName: props.user ? `${props.user.last_name || ""} ${props.user.first_name || ""}`.trim() : ""
            });
            message.success(newActive ? "お知らせを有効にしました。" : "お知らせを無効にしました。");
            loadNotifications();
        } catch (err) {
            console.error(err);
            message.error("更新中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file limit
        if (existingImageUrls.length + filesToUpload.length >= 2) {
            message.error("添付できるファイルは最大2つまでです。");
            return;
        }

        // Check file size (5MB)
        const MAX_SIZE = 5 * 1024 * 1024;
        if (file.size > MAX_SIZE) {
            message.error("ファイルサイズは5MB以下にしてください。");
            return;
        }

        // Check file type
        const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
        if (!allowedTypes.includes(file.type)) {
            message.error("PDF, PNG, JPEGファイルのみ添付可能です。");
            return;
        }

        setFilesToUpload([...filesToUpload, file]);
        // Reset input value
        e.target.value = "";
    };

    const handleRemoveExistingImage = (index) => {
        const newUrls = [...existingImageUrls];
        newUrls.splice(index, 1);
        setExistingImageUrls(newUrls);
    };

    const handleRemovePendingImage = (index) => {
        const newFiles = [...filesToUpload];
        newFiles.splice(index, 1);
        setFilesToUpload(newFiles);
    };

    const handleFinish = async (values) => {
        if (values.displayStart && values.displayEnd) {
            if (values.displayStart.isAfter(values.displayEnd, 'day')) {
                setDateError(true);
                return;
            }
        }
        setDateError(false);

        try {
            setLoading(true);

            let originalImageUrls = [];
            if (editingId) {
                const currentRecord = notifications.find(n => n.id === editingId);
                originalImageUrls = currentRecord ? (currentRecord.imageUrls || []) : [];
            }

            let imageUrls = [...existingImageUrls];

            if (filesToUpload.length > 0) {
                const uploadPromises = filesToUpload.map(async (file) => {
                    const storageRef = firebase.storage().ref().child(`notification/${Date.now()}_${file.name}`);
                    await storageRef.put(file);
                    const downloadUrl = await storageRef.getDownloadURL();
                    return downloadUrl;
                });
                const newUrls = await Promise.all(uploadPromises);
                imageUrls = [...imageUrls, ...newUrls];
            }

            // Detect and delete removed storage files
            const deletedUrls = originalImageUrls.filter(url => !imageUrls.includes(url));
            if (deletedUrls.length > 0) {
                const deletePromises = deletedUrls.map(url => deleteStorageFile(url));
                await Promise.all(deletePromises);
            }

            const resellerOrgs = values.targetOrgsReseller || [];
            const buyerOrgs = values.targetOrgsBuyer || [];
            const targetRoles = ["reseller", "buyer"];

            // Extract allowed org IDs from current base organization sellers and buyers
            const baseOrgSellers = (baseOrgDetails?.sellers || []).map(s => s.organization);
            const baseOrgBuyers = (baseOrgDetails?.buyers || []).map(b => b.organization);

            // Combine all unique selected organizations into targetOrgs for backwards compatibility (excluding NONE)
            const combinedResellers = resellerOrgs.filter(id => id !== "NONE");
            const combinedBuyers = buyerOrgs.filter(id => id !== "NONE");
            const targetOrgs = Array.from(new Set([...combinedResellers, ...combinedBuyers]));

            const payload = {
                title: values.title || "",
                body: values.body || "",
                displayStart: values.displayStart ? values.displayStart.toDate() : null,
                displayEnd: values.displayEnd ? values.displayEnd.toDate() : null,
                isActive: !!values.isActive,
                baseOrgId: baseOrgId || "",
                baseOrgName: baseOrgName || "",
                baseOrgSellers: baseOrgSellers,
                baseOrgBuyers: baseOrgBuyers,
                targetRoles: targetRoles,
                targetOrgs: targetOrgs,
                targetOrgsMaker: [],
                targetOrgsReseller: resellerOrgs,
                targetOrgsBuyer: buyerOrgs,
                imageUrls: imageUrls,
                userType: "all",
                updatedAt: new Date(),
                updatedBy: props.user ? props.user.id : "",
                updatedByName: props.user ? `${props.user.last_name || ""} ${props.user.first_name || ""}`.trim() : ""
            };

            if (editingId) {
                await db.collection(COLLECTION_NAME).doc(editingId).set(payload, { merge: true });
                message.success("お知らせを更新しました。");
            } else {
                payload.createdAt = new Date();
                payload.createdBy = props.user ? props.user.id : "";
                payload.createdByName = props.user ? `${props.user.last_name || ""} ${props.user.first_name || ""}`.trim() : "";
                await db.collection(COLLECTION_NAME).add(payload);
                message.success("お知らせを作成しました。");
            }

            setMode("list");
            loadNotifications();
        } catch (err) {
            console.error(err);
            message.error("保存中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    };

    if (props.user && !props.user.admin) {
        return <div style={{ padding: 24, textAlign: "center" }}>permission required</div>;
    };

    const selectCustomStyle = `
        .custom-line-break-select .ant-select-selection-overflow-item {
            width: 100% !important;
        }
        .custom-line-break-select .ant-select-selection-item {
            width: 100% !important;
            display: flex;
            justify-content: space-between;
        }
        .org-id-text {
            color: #888;
        }
        .ant-select-item-option-selected .org-id-text {
            color: rgba(255, 255, 255, 0.8) !important;
        }
        .notification-table .ant-table-cell {
            vertical-align: top;
        }
    `;

    // ===============================
    // Table Columns
    // ===============================
    const columns = [
        {
            title: "タイトル",
            dataIndex: "title",
            key: "title",
            width: 220,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: "bold" }}>{text}</div>
                </div>
            )
        },
        {
            title: "表示期間",
            key: "period",
            render: (_, record) => {
                const start = record.displayStart ? dayjs(record.displayStart.toDate()).format("YYYY/MM/DD") : "指定なし";
                const end = record.displayEnd ? dayjs(record.displayEnd.toDate()).format("YYYY/MM/DD") : "指定なし";
                return <span>{start} 〜 {end}</span>;
            }
        },
        {
            title: "発信元事業所",
            key: "baseOrg",
            render: (_, record) => {
                const name = record.baseOrgName || "";
                const id = record.baseOrgId || "";
                if (!id && !name) return <span>-</span>;
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{name || id}</div>
                        {id && <div style={{ fontSize: "11px", color: "#888" }}>ID: {id}</div>}
                    </div>
                );
            }
        },
        {
            title: "対象事業所",
            dataIndex: "targetOrgs",
            key: "targetOrgs",
            render: (orgIds, record) => {
                const categories = [
                    { key: "reseller", label: "販売側事業所", color: "orange", specificOrgs: record.targetOrgsReseller || [] },
                    { key: "buyer", label: "仕入側事業所", color: "green", specificOrgs: record.targetOrgsBuyer || [] }
                ];

                const renderedItems = [];

                categories.forEach(cat => {
                    if (cat.specificOrgs.length === 1 && cat.specificOrgs[0] === "NONE") {
                        renderedItems.push(
                            <div key={`none-${cat.key}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                                <Tag 
                                    color="magenta" 
                                    style={{ margin: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                                    onClick={() => handleOpenMapModal(record, cat.key)}
                                    title="クリックして対象事業所一覧の確認・変更"
                                >
                                    <span>対象なし (配信なし)</span>
                                    <UnorderedListOutlined style={{ fontSize: 11 }} />
                                </Tag>
                                <Tag color={cat.color} style={{ margin: 0 }}>{cat.label}</Tag>
                            </div>
                        );
                    } else if (cat.specificOrgs.length === 0) {
                        renderedItems.push(
                            <div key={`all-${cat.key}`} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                                <Tag 
                                    color="default" 
                                    style={{ margin: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
                                    onClick={() => handleOpenMapModal(record, cat.key)}
                                    title="クリックして対象事業所一覧の確認・選択解除"
                                >
                                    <span>map内の全事業所</span>
                                    <UnorderedListOutlined style={{ fontSize: 11 }} />
                                </Tag>
                                <Tag color={cat.color} style={{ margin: 0 }}>{cat.label}</Tag>
                            </div>
                        );
                    } else {
                        cat.specificOrgs.forEach(id => {
                            if (id === "NONE") return;
                            const found = organizations.find(o => o.id === id);
                            const fullName = found ? `${found.company_name} ${found.name}`.trim() : id;
                            renderedItems.push(
                                <div key={`${cat.key}-${id}`} style={{ whiteSpace: "nowrap", display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span>{fullName} (ID: {id})</span>
                                    <Tag color={cat.color} style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>{cat.label}</Tag>
                                </div>
                            );
                        });
                    }
                });

                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {renderedItems}
                    </div>
                );
            }
        },
        {
            title: "作成日時",
            key: "createdAt",
            render: (_, record) => {
                return <span>{record.createdAt ? dayjs(record.createdAt.toDate()).format("YYYY/MM/DD HH:mm") : "-"}</span>;
            }
        },
        {
            title: "登録者",
            key: "creator",
            render: (_, record) => (
                <div>
                    <div>{record.createdByName || "-"}</div>
                    <div style={{ fontSize: "11px", color: "#888" }}>{record.createdBy || "-"}</div>
                </div>
            )
        },
        {
            title: "最終更新日時",
            key: "updatedAt",
            render: (_, record) => {
                return <span>{record.updatedAt ? dayjs(record.updatedAt.toDate()).format("YYYY/MM/DD HH:mm") : "-"}</span>;
            }
        },
        {
            title: "最終更新者",
            key: "updater",
            render: (_, record) => (
                <div>
                    <div>{record.updatedByName || "-"}</div>
                    <div style={{ fontSize: "11px", color: "#888" }}>{record.updatedBy || "-"}</div>
                </div>
            )
        },
        {
            title: "状態",
            dataIndex: "isActive",
            key: "isActive",
            fixed: "right",
            width: 90,
            render: (isActive, record) => (
                <Button 
                    type={isActive ? "primary" : "default"} 
                    size="small"
                    onClick={() => handleToggleActive(record)}
                >
                    {isActive ? "有効" : "無効"}
                </Button>
            )
        },
        {
            title: "操作",
            key: "actions",
            fixed: "right",
            width: 180,
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEditClick(record)}>編集</Button>
                    <Popconfirm
                        title="お知らせを削除しますか？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="はい"
                        cancelText="いいえ"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>削除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const tagRenderHelper = (props) => {
        const { value, label, closable, onClose } = props;
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        if (value === "NONE") {
            return (
                <div style={{ width: '100%' }}>
                    <Tag
                        color="red"
                        onMouseDown={onPreventMouseDown}
                        closable={closable}
                        onClose={onClose}
                        style={{ marginRight: 3, marginTop: 2, marginBottom: 2, fontWeight: "bold" }}
                    >
                        【対象なし】この区分の事業所には配信しない
                    </Tag>
                </div>
            );
        }
        return (
            <div style={{ width: '100%' }}>
                <Tag
                    onMouseDown={onPreventMouseDown}
                    closable={closable}
                    onClose={onClose}
                    style={{ marginRight: 3, marginTop: 2, marginBottom: 2 }}
                >
                    {label}
                </Tag>
            </div>
        );
    };

    const handleSelectOrgsChange = (fieldName, selectedValues) => {
        if (!selectedValues || selectedValues.length === 0) {
            form.setFieldsValue({ [fieldName]: [] });
            return;
        }
        if (selectedValues.includes("NONE")) {
            const currentFieldVal = form.getFieldValue(fieldName) || [];
            if (!currentFieldVal.includes("NONE")) {
                form.setFieldsValue({ [fieldName]: ["NONE"] });
            } else if (selectedValues.length > 1) {
                form.setFieldsValue({ [fieldName]: selectedValues.filter(val => val !== "NONE") });
            }
        }
    };

    const baseOrgName = baseOrgDetails ? `${baseOrgDetails.company_name || ""} ${baseOrgDetails.name || ""}`.trim() : (props.orgSeller?.name || props.userOrg?.name || "");
    const baseOrgId = baseOrgDetails?.id || props.orgSeller?.id || props.userOrg?.id || "";

    return (
        <div style={{ maxWidth: 1400, margin: "20px auto", padding: 24, background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <style>{selectCustomStyle}</style>

            {/* Selected Base Organization Display */}
            {baseOrgId && (
                <div style={{
                    marginBottom: 20,
                    padding: "12px 16px",
                    background: "#e6f7ff",
                    border: "1px solid #91d5ff",
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 12
                }}>
                    <span style={{ fontSize: 13, color: "#0050b3", fontWeight: "bold" }}>選択中の自社事業所:</span>
                    <span style={{ fontSize: 14, color: "#003a8c", fontWeight: 600 }}>
                        {baseOrgName || baseOrgId}
                    </span>
                    <Tag color="blue" style={{ margin: 0 }}>ID: {baseOrgId}</Tag>
                </div>
            )}

            {mode === "list" ? (
                <>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                        <Col>
                            <Title level={3} style={{ margin: 0 }}>お知らせ管理一覧</Title>
                        </Col>
                        <Col>
                            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleCreateClick}>
                                新規お知らせ作成
                            </Button>
                        </Col>
                    </Row>
                    <Table 
                        className="notification-table"
                        size="small"
                        dataSource={notifications} 
                        columns={columns} 
                        rowKey="id" 
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: "max-content" }}
                    />
                </>
            ) : (
                <>
                    <Row style={{ marginBottom: 24 }}>
                        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setMode("list")}>
                            一覧に戻る
                        </Button>
                    </Row>
                    <Title level={3}>{editingId ? "お知らせ編集" : "新規お知らせ作成"}</Title>
                    {(() => {
                        const editingRecord = notifications.find(n => n.id === editingId);
                        if (!editingRecord) return null;
                        return (
                            <div style={{ marginBottom: 24, padding: 16, background: "#f5f5f5", borderRadius: 8 }}>
                                <Row gutter={[16, 8]}>
                                    <Col span={8}>
                                        <div><strong>発信元事業所:</strong> {editingRecord.baseOrgName || "-"}</div>
                                        <div><strong>発信元事業所ID:</strong> {editingRecord.baseOrgId || "-"}</div>
                                    </Col>
                                    <Col span={8}>
                                        <div><strong>登録者名:</strong> {editingRecord.createdByName || "-"}</div>
                                        <div><strong>登録者ID:</strong> {editingRecord.createdBy || "-"}</div>
                                        <div><strong>登録日時:</strong> {editingRecord.createdAt ? dayjs(editingRecord.createdAt.toDate()).format("YYYY/MM/DD HH:mm") : "-"}</div>
                                    </Col>
                                    <Col span={8}>
                                        <div><strong>最終更新者名:</strong> {editingRecord.updatedByName || "-"}</div>
                                        <div><strong>最終更新者ID:</strong> {editingRecord.updatedBy || "-"}</div>
                                        <div><strong>最終更新日時:</strong> {editingRecord.updatedAt ? dayjs(editingRecord.updatedAt.toDate()).format("YYYY/MM/DD HH:mm") : "-"}</div>
                                    </Col>
                                </Row>
                            </div>
                        );
                    })()}
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFinish}
                        requiredMark={false}
                    >
                        <Form.Item label="タイトル" name="title" rules={[{ required: true, message: "必須項目です" }]}>
                            <Input placeholder="お知らせのタイトルを入力" />
                        </Form.Item>

                        <Form.Item label="本文" name="body" rules={[{ required: true, message: "必須項目です" }]}>
                            <TextArea rows={8} placeholder="お知らせの本文を入力してください" />
                        </Form.Item>

                        {/* 表示期間 */}
                        <Form.Item label="表示期間" style={{ marginBottom: 24 }}>
                            <Row gutter={16} align="middle">
                                <Col span={11}>
                                    <Form.Item name="displayStart" noStyle>
                                        <DatePicker style={{ width: "100%" }} placeholder="開始日" />
                                    </Form.Item>
                                </Col>
                                <Col span={2} style={{ textAlign: "center" }}>〜</Col>
                                <Col span={11}>
                                    <Form.Item name="displayEnd" noStyle>
                                        <DatePicker style={{ width: "100%" }} placeholder="終了日" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {dateError && (
                                <div style={{ color: "red", marginTop: 8, fontSize: "14px" }}>
                                    終了日に開始日よりも前の日付が指定されています
                                </div>
                            )}
                        </Form.Item>

                        {/* 対象ユーザーのロールと事業所 */}
                        <div style={{
                            border: "1px solid #d9d9d9",
                            borderRadius: "8px",
                            padding: "24px",
                            marginBottom: "24px",
                            background: "#fafafa"
                        }}>
                            <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "16px", color: "#333" }}>
                                対象事業所の選択
                            </div>

                            {/* 販売側事業所 (Sales side / Reseller) */}
                            <div style={{
                                background: "#fff",
                                border: "1px solid #f0f0f0",
                                borderRadius: "8px",
                                padding: "16px",
                                marginBottom: "16px",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
                            }}>
                                <div style={{ fontSize: "15px", fontWeight: "bold", marginBottom: 8 }}>
                                    販売側事業所
                                </div>
                                <div style={{ fontSize: "13px", color: "#666", marginBottom: 8 }}>
                                    対象事業所 (特定事業所のみに配信する場合は選択、未選択時はmap内の全事業所)
                                </div>
                                {(() => {
                                    let resellerOptions = [];
                                    if (baseOrgDetails && baseOrgDetails.sellers && Array.isArray(baseOrgDetails.sellers)) {
                                        resellerOptions = baseOrgDetails.sellers.map(s => {
                                            const found = organizations.find(org => org.id === s.organization);
                                            return {
                                                id: s.organization,
                                                company_name: found ? found.company_name : "",
                                                name: found ? found.name : s.name || "",
                                                maker: found ? found.maker : false,
                                                reseller: found ? found.reseller : true,
                                                buyer: found ? found.buyer : false,
                                            };
                                        });
                                    } else {
                                        resellerOptions = organizations.filter(org => org.reseller);
                                    }

                                    return (
                                        <Form.Item name="targetOrgsReseller" style={{ marginBottom: 0 }}>
                                            <Select
                                                className="custom-line-break-select"
                                                mode="multiple"
                                                allowClear
                                                style={{ width: "100%" }}
                                                placeholder="すべての事業所を対象とする場合は空欄のまま（「対象なし」を選択すると配信されません）"
                                                optionFilterProp="label"
                                                tagRender={tagRenderHelper}
                                                onChange={(vals) => handleSelectOrgsChange("targetOrgsReseller", vals)}
                                            >
                                                <Select.Option key="NONE" value="NONE" label="【対象なし】この区分の事業所には配信しない">
                                                    <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                                                        【対象なし】この区分の事業所には配信しない
                                                    </span>
                                                </Select.Option>
                                                {resellerOptions.map(org => {
                                                    const fullName = `${org.company_name} ${org.name}`.trim();
                                                    const label = `${fullName} (${org.id})`;
                                                    return (
                                                        <Select.Option key={org.id} value={org.id} label={label}>
                                                            {fullName}
                                                            <span className="org-id-text" style={{ fontSize: "11px", marginLeft: 8, marginRight: 8 }}>
                                                                (ID: {org.id})
                                                            </span>
                                                            {org.maker && <Tag color="red" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>メーカーU</Tag>}
                                                            {org.reseller && <Tag color="orange" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>販社U</Tag>}
                                                            {org.buyer && <Tag color="green" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>購入U</Tag>}
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select>
                                        </Form.Item>
                                    );
                                })()}
                            </div>

                            {/* 仕入側事業所 (Purchase side / Buyer) */}
                            <div style={{
                                background: "#fff",
                                border: "1px solid #f0f0f0",
                                borderRadius: "8px",
                                padding: "16px",
                                marginBottom: 0,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
                            }}>
                                <div style={{ fontSize: "15px", fontWeight: "bold", marginBottom: 8 }}>
                                    仕入側事業所
                                </div>
                                <div style={{ fontSize: "13px", color: "#666", marginBottom: 8 }}>
                                    対象事業所 (特定事業所のみに配信する場合は選択、未選択時はmap内の全事業所)
                                </div>
                                {(() => {
                                    let buyerOptions = [];
                                    if (baseOrgDetails && baseOrgDetails.buyers && Array.isArray(baseOrgDetails.buyers)) {
                                        buyerOptions = baseOrgDetails.buyers.map(b => {
                                            const found = organizations.find(org => org.id === b.organization);
                                            return {
                                                id: b.organization,
                                                company_name: found ? found.company_name : "",
                                                name: found ? found.name : b.name || "",
                                                maker: found ? found.maker : false,
                                                reseller: found ? found.reseller : false,
                                                buyer: found ? found.buyer : true,
                                            };
                                        });
                                    } else {
                                        buyerOptions = organizations.filter(org => org.buyer);
                                    }

                                    return (
                                        <Form.Item name="targetOrgsBuyer" style={{ marginBottom: 0 }}>
                                            <Select
                                                className="custom-line-break-select"
                                                mode="multiple"
                                                allowClear
                                                style={{ width: "100%" }}
                                                placeholder="すべての事業所を対象とする場合は空欄のまま（「対象なし」を選択すると配信されません）"
                                                optionFilterProp="label"
                                                tagRender={tagRenderHelper}
                                                onChange={(vals) => handleSelectOrgsChange("targetOrgsBuyer", vals)}
                                            >
                                                <Select.Option key="NONE" value="NONE" label="【対象なし】この区分の事業所には配信しない">
                                                    <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                                                        【対象なし】この区分の事業所には配信しない
                                                    </span>
                                                </Select.Option>
                                                {buyerOptions.map(org => {
                                                    const fullName = `${org.company_name} ${org.name}`.trim();
                                                    const label = `${fullName} (${org.id})`;
                                                    return (
                                                        <Select.Option key={org.id} value={org.id} label={label}>
                                                            {fullName}
                                                            <span className="org-id-text" style={{ fontSize: "11px", marginLeft: 8, marginRight: 8 }}>
                                                                (ID: {org.id})
                                                            </span>
                                                            {org.maker && <Tag color="red" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>メーカーU</Tag>}
                                                            {org.reseller && <Tag color="orange" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>販社U</Tag>}
                                                            {org.buyer && <Tag color="green" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>購入U</Tag>}
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select>
                                        </Form.Item>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* 添付ファイル */}
                        <Form.Item label="添付ファイル (PDF, PNG, JPEG - 最大2ファイル、各5MBまで)">
                            <div style={{ marginBottom: 12 }}>
                                {existingImageUrls.map((url, index) => {
                                    // Extract filename from firebase download url
                                    const decodedUrl = decodeURIComponent(url);
                                    const parts = decodedUrl.split("/");
                                    const rawFileName = parts[parts.length - 1].split("?")[0];
                                    const cleanFileName = rawFileName.substring(rawFileName.indexOf("_") + 1);
                                    return (
                                        <div key={index} style={{ display: "flex", alignHover: "center", gap: 8, marginBottom: 6 }}>
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                {cleanFileName || `添付ファイル ${index + 1}`}
                                            </a>
                                            <Popconfirm
                                                title="ファイルを削除しますか？"
                                                onConfirm={() => handleRemoveExistingImage(index)}
                                                okText="はい"
                                                cancelText="いいえ"
                                            >
                                                <Button size="small" danger type="link">
                                                    削除
                                                </Button>
                                            </Popconfirm>
                                        </div>
                                    );
                                })}
                                {filesToUpload.map((file, index) => (
                                    <div key={index} style={{ display: "flex", alignHover: "center", gap: 8, marginBottom: 6 }}>
                                        <span>{file.name} (アップロード待ち)</span>
                                        <Popconfirm
                                            title="アップロード待ちのファイルを削除しますか？"
                                            onConfirm={() => handleRemovePendingImage(index)}
                                            okText="はい"
                                            cancelText="いいえ"
                                        >
                                            <Button size="small" danger type="link">
                                                削除
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                ))}
                            </div>
                            {existingImageUrls.length + filesToUpload.length < 2 && (
                                <input
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg"
                                    onChange={handleFileChange}
                                    style={{ display: "block" }}
                                />
                            )}
                        </Form.Item>

                        {/* 公開状態 */}
                        <Form.Item label="公開状態" name="isActive" valuePropName="checked">
                            <Checkbox>有効</Checkbox>
                        </Form.Item>

                        {/* Buttons */}
                        <Form.Item style={{ marginTop: 40, textAlign: "center" }}>
                            <Space size={40}>
                                <Button style={{ minWidth: 160 }} onClick={() => setMode("list")} disabled={loading}>
                                    キャンセル
                                </Button>
                                <Button type="primary" htmlType="submit" style={{ minWidth: 160 }} loading={loading}>
                                    保存
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </>
            )}

            <Modal
                title="確認"
                open={!!pendingOrgChange}
                onOk={handleConfirmOrgChange}
                onCancel={handleCancelOrgChange}
                okText="確認"
                cancelText="キャンセル"
            >
                <p style={{ fontSize: "14px", lineHeight: "1.6" }}>
                    自社事業所（発信元）を変更すると、配信先事業所の選択は一度すべてクリアされます。その際、このお知らせ設定は「タイトル」と「本文」以外を最初から設定することになります。
                </p>
            </Modal>

            {/* Map Facilities Modal */}
            <Modal
                title={`「map内の全事業所」の対象事業所一覧 (${mapModalState.category === "reseller" ? "販売側事業所" : "仕入側事業所"})`}
                open={mapModalState.open}
                onOk={handleSaveMapModal}
                onCancel={() => setMapModalState(prev => ({ ...prev, open: false }))}
                okText="保存"
                cancelText="キャンセル"
                confirmLoading={mapModalState.saving}
                width={700}
            >
                {mapModalState.loading ? (
                    <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
                ) : (
                    <div>
                        <div style={{ marginBottom: 12, fontSize: 13, color: "#666" }}>
                            チェックを外すことで、対象事業所から除外（選択解除）できます。
                        </div>

                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Col span={14}>
                                <Input
                                    placeholder="事業所名・IDで検索"
                                    value={mapModalState.searchText}
                                    onChange={(e) => setMapModalState(prev => ({ ...prev, searchText: e.target.value }))}
                                    allowClear
                                />
                            </Col>
                            <Col span={9} style={{ textAlign: "right" }}>
                                <Checkbox
                                    checked={
                                        mapModalState.facilities.length > 0 &&
                                        mapModalState.facilities.every(f => mapModalState.checkedOrgIds.includes(f.id))
                                    }
                                    indeterminate={
                                        mapModalState.facilities.some(f => mapModalState.checkedOrgIds.includes(f.id)) &&
                                        !mapModalState.facilities.every(f => mapModalState.checkedOrgIds.includes(f.id))
                                    }
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setMapModalState(prev => ({
                                                ...prev,
                                                checkedOrgIds: prev.facilities.map(f => f.id)
                                            }));
                                        } else {
                                            setMapModalState(prev => ({
                                                ...prev,
                                                checkedOrgIds: []
                                            }));
                                        }
                                    }}
                                >
                                    すべて選択
                                </Checkbox>
                            </Col>
                        </Row>

                        <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: "8px 12px" }}>
                            {mapModalState.facilities
                                .filter(f => {
                                    if (!mapModalState.searchText) return true;
                                    const q = mapModalState.searchText.toLowerCase();
                                    const fullName = `${f.company_name} ${f.name}`.toLowerCase();
                                    return fullName.includes(q) || f.id.toLowerCase().includes(q);
                                })
                                .map(org => {
                                    const isChecked = mapModalState.checkedOrgIds.includes(org.id);
                                    const fullName = `${org.company_name} ${org.name}`.trim();
                                    return (
                                        <div 
                                            key={org.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "8px 0",
                                                borderBottom: "1px solid #fafafa"
                                            }}
                                        >
                                            <Checkbox
                                                checked={isChecked}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setMapModalState(prev => {
                                                        const newIds = checked
                                                            ? [...prev.checkedOrgIds, org.id]
                                                            : prev.checkedOrgIds.filter(id => id !== org.id);
                                                        return { ...prev, checkedOrgIds: newIds };
                                                    });
                                                }}
                                                style={{ marginRight: 12 }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontWeight: 500, fontSize: 14 }}>{fullName}</span>
                                                <span style={{ fontSize: 11, color: "#888", marginLeft: 8 }}>
                                                    (ID: {org.id})
                                                </span>
                                            </div>
                                            <Space size={4}>
                                                {org.maker && <Tag color="red" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>メーカーU</Tag>}
                                                {org.reseller && <Tag color="orange" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>販社U</Tag>}
                                                {org.buyer && <Tag color="green" style={{ fontSize: "10px", lineHeight: "16px", padding: "0 4px" }}>購入U</Tag>}
                                            </Space>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Notification;
